// Interactive props and pickups: doors, exit gate, crate, terminal, service station,
// repair bay, hologram pedestal, scrap, health capsule. Gameplay objects carry a dark
// ink outline (art bible §4) and are lit from the upper left.

import type Phaser from 'phaser';
import { COLORS, mix } from '../../config/palette';
import { TEX } from '../keys';
import {
  TAU,
  addGridFrames,
  circlePath,
  hexPath,
  neonStroke,
  newCanvas,
  polyPath,
  px,
  rgba,
  roundRectPath,
  softDot,
  softEllipse,
  tone,
  type G,
} from './canvas';

const INK = COLORS.ink;
/** Exit / "go" signal colour: between the UI good-green and cyan so it never reads as loot or threat. */
export const EXIT_COLOR = mix(COLORS.uiGood, COLORS.volt, 0.45);

export function buildProps(scene: Phaser.Scene): void {
  door(scene);
  exitGate(scene);
  crate(scene);
  terminal(scene);
  station(scene);
  repairBay(scene);
  holoBase(scene);
  scrap(scene);
  health(scene);
}

// ───────────────────────────── Shared painters ─────────────────────────────

/** Metal plate: vertical gradient body, lit top/left edges, ink outline. */
export function plate(g: G, x: number, y: number, w: number, h: number, base: number, r = 3, outline = 2): void {
  roundRectPath(g, x, y, w, h, r);
  const grad = g.createLinearGradient(x, y, x + w * 0.35, y + h);
  grad.addColorStop(0, rgba(tone(base, 0.22)));
  grad.addColorStop(0.45, rgba(base));
  grad.addColorStop(1, rgba(tone(base, -0.35)));
  g.fillStyle = grad;
  g.fill();
  if (outline > 0) {
    g.strokeStyle = rgba(INK);
    g.lineWidth = outline;
    g.stroke();
  }
  // Key-light bevel on the top edge.
  g.save();
  roundRectPath(g, x, y, w, h, r);
  g.clip();
  g.fillStyle = rgba(tone(base, 0.5), 0.55);
  g.fillRect(x, y + outline * 0.5, w, 1.4);
  g.fillStyle = rgba(tone(base, 0.35), 0.35);
  g.fillRect(x + outline * 0.5, y, 1.2, h);
  g.restore();
}

function bolt(g: G, x: number, y: number, r = 1.6): void {
  circlePath(g, x, y, r);
  g.fillStyle = rgba(INK, 0.85);
  g.fill();
  circlePath(g, x - 0.4, y - 0.4, r * 0.5);
  g.fillStyle = rgba(COLORS.steel5, 0.6);
  g.fill();
}

/** Small emissive indicator lamp with a soft halo. */
function lamp(g: G, x: number, y: number, color: number, r = 2.2, halo = 7, alpha = 1): void {
  softDot(g, x, y, halo, color, 0.45 * alpha);
  circlePath(g, x, y, r);
  g.fillStyle = rgba(color, alpha);
  g.fill();
  circlePath(g, x - r * 0.25, y - r * 0.25, r * 0.45);
  g.fillStyle = rgba(0xffffff, 0.8 * alpha);
  g.fill();
}

/** Diagonal hazard stripes clipped to a rect. */
function hazardStripes(g: G, x: number, y: number, w: number, h: number, a: number, b: number, period = 10): void {
  g.save();
  g.beginPath();
  g.rect(x, y, w, h);
  g.clip();
  g.fillStyle = rgba(b);
  g.fillRect(x, y, w, h);
  g.fillStyle = rgba(a);
  for (let k = x - h - period; k < x + w + h; k += period) {
    g.beginPath();
    g.moveTo(k, y + h);
    g.lineTo(k + period / 2, y + h);
    g.lineTo(k + period / 2 + h, y);
    g.lineTo(k + h, y);
    g.closePath();
    g.fill();
  }
  g.restore();
}

/** Screen with a glowing face, scan lines and abstract UI bars (never letters). */
function screen(g: G, x: number, y: number, w: number, h: number, color: number, seed = 0): void {
  roundRectPath(g, x - 2, y - 2, w + 4, h + 4, 3);
  g.fillStyle = rgba(INK);
  g.fill();
  roundRectPath(g, x, y, w, h, 2);
  const grad = g.createLinearGradient(x, y, x, y + h);
  grad.addColorStop(0, rgba(mix(color, INK, 0.55)));
  grad.addColorStop(1, rgba(mix(color, INK, 0.82)));
  g.fillStyle = grad;
  g.fill();
  g.save();
  roundRectPath(g, x, y, w, h, 2);
  g.clip();
  // Abstract readout bars.
  const rows = Math.max(2, Math.floor((h - 6) / 6));
  for (let i = 0; i < rows; i++) {
    const len = (0.3 + (((i * 37 + seed * 13) % 11) / 11) * 0.6) * (w - 8);
    g.fillStyle = rgba(color, i === 0 ? 0.95 : 0.55);
    g.fillRect(x + 4, y + 4 + i * 6, len, 2);
  }
  for (let sy = y; sy < y + h; sy += 2) {
    g.fillStyle = rgba(0x000000, 0.18);
    g.fillRect(x, sy, w, 1);
  }
  // Glass glare.
  g.fillStyle = rgba(0xffffff, 0.07);
  g.beginPath();
  g.moveTo(x, y);
  g.lineTo(x + w * 0.45, y);
  g.lineTo(x + w * 0.2, y + h);
  g.lineTo(x, y + h);
  g.closePath();
  g.fill();
  g.restore();
  softEllipse(g, x + w / 2, y + h / 2, w * 0.7, h * 0.7, color, 0.12);
}

// ───────────────────────────── Door ─────────────────────────────

function door(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.door, 96, 144);
  if (!c) return;
  for (let f = 0; f < 2; f++) {
    const g = c.g;
    g.save();
    g.translate(f * 48, 0);
    g.beginPath();
    g.rect(0, 0, 48, 144);
    g.clip();
    const locked = f === 0;
    // Side rails.
    for (const x of [2, 39]) {
      plate(g, x, 16, 7, 112, COLORS.steel2, 2, 2);
      for (let y = 26; y < 124; y += 16) bolt(g, x + 3.5, y, 1.2);
    }
    if (locked) {
      // Energy field + bars between the rails.
      const field = g.createLinearGradient(9, 0, 39, 0);
      field.addColorStop(0, rgba(COLORS.threat, 0.32));
      field.addColorStop(0.5, rgba(COLORS.threat, 0.12));
      field.addColorStop(1, rgba(COLORS.threat, 0.32));
      g.fillStyle = field;
      g.fillRect(9, 22, 30, 100);
      for (let i = 0; i < 6; i++) {
        const y = 30 + i * 17;
        g.beginPath();
        g.moveTo(9, y);
        g.lineTo(39, y);
        neonStroke(g, COLORS.threat, 2, 3.5, 1, 0xffffff, 0.55);
      }
      // Faint vertical scan lines.
      for (let x = 13; x < 38; x += 6) px(g, x, 22, 1, 100, COLORS.threatGlow, 0.12);
    }
    // Top / bottom emitter housings.
    for (const [y, flip] of [
      [0, false],
      [124, true],
    ] as const) {
      plate(g, 1, y + 1, 46, 19, COLORS.steel2, 3, 2);
      hazardStripes(g, 5, flip ? y + 4 : y + 12, 38, 4, 0xa88626, 0x14171c, 8);
      // Emitter slot facing the doorway.
      const sy = flip ? y + 1 : y + 16;
      px(g, 9, sy, 30, 3, INK);
      px(g, 10, sy + (flip ? 0 : 1), 28, 1, locked ? COLORS.threat : COLORS.steel3, locked ? 0.95 : 0.6);
      lamp(g, 40, flip ? y + 14 : y + 7, locked ? COLORS.threat : COLORS.uiGood, 1.8, 6);
      bolt(g, 6, flip ? y + 14 : y + 7, 1.3);
    }
    if (!locked) {
      // Retracted bar stubs.
      for (const y of [21, 120]) px(g, 12, y, 24, 2, COLORS.steel3, 0.7);
    }
    g.restore();
  }
  addGridFrames(c.tex, 48, 144, 2, 2);
  c.tex.refresh();
}

// ───────────────────────────── Exit gate ─────────────────────────────

function exitGate(scene: Phaser.Scene): void {
  const W = 160;
  const H = 208;
  const c = newCanvas(scene, TEX.exitGate, W * 2, H);
  if (!c) return;
  for (let f = 0; f < 2; f++) {
    const g = c.g;
    const active = f === 1;
    g.save();
    g.translate(f * W, 0);
    g.beginPath();
    g.rect(0, 0, W, H);
    g.clip();
    const cx = W / 2;
    const col = EXIT_COLOR;

    // Portal interior (behind the frame).
    const px0 = 34;
    const py0 = 40;
    const pw = W - 68;
    const ph = 150;
    g.save();
    roundRectPath(g, px0, py0, pw, ph, 30);
    g.clip();
    if (active) {
      const grad = g.createRadialGradient(cx, py0 + ph * 0.55, 4, cx, py0 + ph * 0.55, ph * 0.7);
      grad.addColorStop(0, rgba(0xffffff, 1));
      grad.addColorStop(0.12, rgba(tone(col, 0.6), 1));
      grad.addColorStop(0.45, rgba(col, 0.75));
      grad.addColorStop(1, rgba(mix(col, INK, 0.7), 0.95));
      g.fillStyle = grad;
      g.fillRect(px0, py0, pw, ph);
      // Swirl bands.
      for (let k = 0; k < 7; k++) {
        g.beginPath();
        const r = 14 + k * 9;
        g.ellipse(cx, py0 + ph * 0.55, r * 0.7, r, 0, k * 0.7, k * 0.7 + Math.PI * 1.1);
        g.strokeStyle = rgba(0xffffff, 0.22 - k * 0.02);
        g.lineWidth = 2;
        g.stroke();
      }
      // Upward light streaks.
      for (let i = 0; i < 9; i++) {
        const x = px0 + 8 + ((i * 29) % (pw - 16));
        const y = py0 + 20 + ((i * 47) % (ph - 50));
        g.fillStyle = rgba(0xffffff, 0.35);
        g.fillRect(x, y, 1.5, 12 + (i % 3) * 6);
      }
    } else {
      const grad = g.createLinearGradient(0, py0, 0, py0 + ph);
      grad.addColorStop(0, rgba(0x0a0f16));
      grad.addColorStop(1, rgba(0x05070b));
      g.fillStyle = grad;
      g.fillRect(px0, py0, pw, ph);
      // Dormant field lines.
      for (let y = py0 + 8; y < py0 + ph; y += 10) px(g, px0, y, pw, 1, col, 0.06);
    }
    g.restore();

    // Pillars.
    for (const side of [-1, 1]) {
      const x = side < 0 ? 6 : W - 34;
      plate(g, x, 34, 28, 166, COLORS.steel2, 4, 2);
      // Inset light strip facing the portal.
      const sx = side < 0 ? x + 20 : x + 4;
      px(g, sx - 1, 46, 6, 140, INK);
      if (active) {
        g.beginPath();
        g.moveTo(sx + 2, 48);
        g.lineTo(sx + 2, 184);
        neonStroke(g, col, 2, 4, 1, 0xffffff, 0.6);
      } else {
        px(g, sx + 1, 48, 2, 136, COLORS.steel3, 0.5);
      }
      // Pillar plate breaks + bolts.
      for (const y of [78, 122, 166]) {
        px(g, x + 2, y, 24, 1, INK, 0.8);
        px(g, x + 2, y + 1, 24, 1, COLORS.steel3, 0.4);
      }
      bolt(g, side < 0 ? x + 7 : x + 21, 44);
      bolt(g, side < 0 ? x + 7 : x + 21, 190);
    }
    // Arch header.
    g.beginPath();
    g.moveTo(4, 44);
    g.lineTo(18, 8);
    g.lineTo(W - 18, 8);
    g.lineTo(W - 4, 44);
    g.lineTo(W - 34, 44);
    g.lineTo(W - 44, 30);
    g.lineTo(44, 30);
    g.lineTo(34, 44);
    g.closePath();
    const hg = g.createLinearGradient(0, 8, 0, 44);
    hg.addColorStop(0, rgba(COLORS.steel4));
    hg.addColorStop(0.25, rgba(COLORS.steel3));
    hg.addColorStop(1, rgba(COLORS.steel1));
    g.fillStyle = hg;
    g.fill();
    g.strokeStyle = rgba(INK);
    g.lineWidth = 2;
    g.stroke();
    // Header chevrons pointing up (route onward).
    for (let i = 0; i < 3; i++) {
      const y = 25 - i * 0;
      const x = cx - 22 + i * 22;
      g.beginPath();
      g.moveTo(x - 7, y + 4);
      g.lineTo(x, y - 4);
      g.lineTo(x + 7, y + 4);
      if (active) neonStroke(g, col, 2.4, 3, 1, 0xffffff, 0.5);
      else {
        g.strokeStyle = rgba(COLORS.steel1);
        g.lineWidth = 3;
        g.stroke();
      }
    }
    // Base plinth.
    plate(g, 0, H - 12, W, 12, COLORS.steel1, 2, 2);
    hazardStripes(g, 36, H - 9, W - 72, 5, 0x8f7426, 0x121418, 8);
    if (active) {
      softEllipse(g, cx, H - 14, 62, 8, col, 0.55);
      lamp(g, 20, 52, col, 2.2, 8);
      lamp(g, W - 20, 52, col, 2.2, 8);
    } else {
      lamp(g, 20, 52, COLORS.threat, 1.8, 5, 0.7);
      lamp(g, W - 20, 52, COLORS.threat, 1.8, 5, 0.7);
    }
    g.restore();
  }
  addGridFrames(c.tex, W, H, 2, 2);
  c.tex.refresh();
}

// ───────────────────────────── Crate ─────────────────────────────

function crate(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.crate, 64, 64);
  if (!c) return;
  const g = c.g;
  // Body.
  plate(g, 3, 6, 58, 56, 0x3d4652, 3, 2);
  // Side panels with ribs.
  for (const x of [9, 35]) {
    roundRectPath(g, x, 14, 20, 40, 2);
    g.fillStyle = rgba(0x2a313b);
    g.fill();
    for (let y = 18; y < 52; y += 6) {
      px(g, x + 1, y, 18, 2, 0x4d5866);
      px(g, x + 1, y + 2, 18, 1, INK, 0.6);
    }
  }
  // Stencil band.
  hazardStripes(g, 4, 30, 56, 6, 0xb0892c, 0x1a1d22, 9);
  px(g, 4, 29, 56, 1, INK, 0.8);
  px(g, 4, 36, 56, 1, INK, 0.8);
  // Lid.
  plate(g, 1, 2, 62, 10, 0x56606e, 2, 2);
  px(g, 26, 5, 12, 3, INK, 0.9);
  px(g, 27, 6, 10, 1, COLORS.scrap, 0.9);
  // Corner guards.
  for (const [x, y] of [
    [1, 2],
    [53, 2],
    [1, 52],
    [53, 52],
  ]) {
    plate(g, x, y, 10, 10, 0x6a7482, 2, 2);
    bolt(g, x + 5, y + 5, 1.3);
  }
  // Status lamp (breakable: warm, salvage-coloured).
  lamp(g, 32, 46, COLORS.scrap, 1.8, 6);
  c.tex.refresh();
}

// ───────────────────────────── Terminal ─────────────────────────────

function terminal(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.terminal, 64, 96);
  if (!c) return;
  const g = c.g;
  // Neutral cyan face; the runtime glow tints it per challenge state.
  const col = mix(COLORS.volt, COLORS.steel4, 0.35);
  const warn = COLORS.telegraph;

  // Upright cabinet.
  plate(g, 10, 10, 44, 78, COLORS.steel2, 4, 2);
  // Side rails.
  px(g, 13, 16, 2, 66, INK, 0.55);
  px(g, 49, 16, 2, 66, INK, 0.55);
  // Sloped hood.
  polyPath(g, [7, 14, 14, 4, 50, 4, 57, 14]);
  const hg = g.createLinearGradient(0, 4, 0, 14);
  hg.addColorStop(0, rgba(COLORS.steel4));
  hg.addColorStop(1, rgba(COLORS.steel2));
  g.fillStyle = hg;
  g.fill();
  g.strokeStyle = rgba(INK);
  g.lineWidth = 2;
  g.stroke();
  px(g, 18, 8, 28, 1.5, warn, 0.85);

  // Screen in a recessed bezel.
  plate(g, 15, 16, 34, 28, COLORS.steel1, 3, 1.5);
  screen(g, 18, 19, 28, 22, col, 3);

  // Status lamps.
  lamp(g, 22, 49, COLORS.uiGood, 1.4, 4);
  lamp(g, 29, 49, warn, 1.4, 4);
  px(g, 35, 48, 12, 2, INK, 0.7);

  // Keypad shelf.
  polyPath(g, [9, 55, 55, 55, 58, 63, 6, 63]);
  const kg = g.createLinearGradient(0, 55, 0, 63);
  kg.addColorStop(0, rgba(COLORS.steel4));
  kg.addColorStop(1, rgba(COLORS.steel3));
  g.fillStyle = kg;
  g.fill();
  g.strokeStyle = rgba(INK);
  g.lineWidth = 1.5;
  g.stroke();
  for (let i = 0; i < 5; i++) px(g, 13 + i * 8, 57.5, 6, 2.5, i === 3 ? col : COLORS.steel5, i === 3 ? 0.95 : 0.6);

  // Lower vents.
  for (let i = 0; i < 4; i++) px(g, 19, 68 + i * 4, 26, 2, INK, 0.65);

  // Base with hazard trim.
  plate(g, 6, 86, 52, 10, COLORS.steel1, 2, 2);
  hazardStripes(g, 10, 89, 44, 4, 0x8f7426, 0x121418, 8);

  // Antenna.
  px(g, 47, 0, 2, 5, COLORS.steel3);
  lamp(g, 48, 1.5, COLORS.threat, 1.3, 4);
  c.tex.refresh();
}

// ───────────────────────────── Service station ─────────────────────────────

function station(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.station, 192, 192);
  if (!c) return;
  const g = c.g;
  const col = COLORS.scrap;
  const cyan = mix(COLORS.volt, COLORS.steel4, 0.35);

  // Back frame + canopy.
  plate(g, 18, 26, 156, 148, COLORS.steel1, 6, 2);
  polyPath(g, [8, 30, 28, 8, 164, 8, 184, 30]);
  const cg = g.createLinearGradient(0, 8, 0, 30);
  cg.addColorStop(0, rgba(COLORS.steel4));
  cg.addColorStop(1, rgba(COLORS.steel2));
  g.fillStyle = cg;
  g.fill();
  g.strokeStyle = rgba(INK);
  g.lineWidth = 2;
  g.stroke();
  // Canopy light strip (warm salvage colour = shop).
  g.beginPath();
  g.moveTo(34, 24);
  g.lineTo(158, 24);
  neonStroke(g, col, 2, 4, 0.95, 0xffffff, 0.5);
  // Canopy sign: abstract emblem (gear + wrench silhouette), no letters.
  circlePath(g, 96, 17, 6.5);
  g.fillStyle = rgba(INK);
  g.fill();
  circlePath(g, 96, 17, 4.6);
  g.strokeStyle = rgba(col);
  g.lineWidth = 2;
  g.stroke();
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * TAU;
    px(g, 96 + Math.cos(a) * 6 - 1, 17 + Math.sin(a) * 6 - 1, 2, 2, col, 0.9);
  }

  // Tool rack (left wall).
  plate(g, 26, 40, 30, 92, COLORS.steel2, 3, 2);
  for (let i = 0; i < 4; i++) {
    const y = 50 + i * 20;
    px(g, 29, y + 10, 24, 2, INK, 0.9);
    // Tools: abstract drills/wrenches.
    g.fillStyle = rgba(i % 2 ? COLORS.steel4 : COLORS.steel5);
    g.fillRect(31 + (i % 2) * 4, y, 3, 10);
    g.fillRect(40, y + 2, 3, 8);
    g.fillStyle = rgba(i === 1 ? COLORS.threatHot : col, 0.85);
    g.fillRect(46, y + 4, 4, 6);
  }
  // Main screen + side screen.
  screen(g, 66, 40, 60, 38, cyan, 1);
  screen(g, 134, 44, 30, 26, col, 4);
  // Counter.
  plate(g, 58, 120, 112, 22, COLORS.steel3, 3, 2);
  px(g, 62, 126, 104, 2, INK, 0.7);
  lamp(g, 70, 134, COLORS.uiGood, 1.6, 5);
  lamp(g, 80, 134, col, 1.6, 5);
  // Parts on the counter.
  plate(g, 132, 110, 18, 10, COLORS.steel2, 2, 1.5);
  circlePath(g, 120, 115, 5);
  g.fillStyle = rgba(COLORS.steel4);
  g.fill();
  g.strokeStyle = rgba(INK);
  g.lineWidth = 1.5;
  g.stroke();

  // Robotic arms hanging from the canopy.
  const arm = (bx: number, ex: number, ey: number, hx: number, hy: number, tip: number) => {
    g.lineCap = 'round';
    for (const [w, colr] of [
      [9, INK],
      [6, COLORS.steel3],
    ] as const) {
      g.strokeStyle = rgba(colr);
      g.lineWidth = w;
      g.beginPath();
      g.moveTo(bx, 32);
      g.lineTo(ex, ey);
      g.lineTo(hx, hy);
      g.stroke();
    }
    g.strokeStyle = rgba(COLORS.steel5, 0.45);
    g.lineWidth = 1.5;
    g.beginPath();
    g.moveTo(bx - 1.5, 32);
    g.lineTo(ex - 1.5, ey);
    g.stroke();
    for (const [x, y] of [
      [bx, 32],
      [ex, ey],
    ]) {
      circlePath(g, x, y, 5);
      g.fillStyle = rgba(COLORS.steel2);
      g.fill();
      g.strokeStyle = rgba(INK);
      g.lineWidth = 2;
      g.stroke();
      circlePath(g, x, y, 1.6);
      g.fillStyle = rgba(cyan, 0.9);
      g.fill();
    }
    // Tool head.
    g.save();
    g.translate(hx, hy);
    g.rotate(Math.atan2(hy - ey, hx - ex));
    plate(g, -2, -4, 12, 8, COLORS.steel2, 2, 1.5);
    px(g, 10, -1.5, 6, 3, COLORS.steel4);
    g.restore();
    lamp(g, hx + Math.cos(Math.atan2(hy - ey, hx - ex)) * 16, hy + Math.sin(Math.atan2(hy - ey, hx - ex)) * 16, tip, 1.5, 6);
  };
  arm(72, 58, 76, 78, 100, cyan);
  arm(152, 170, 80, 150, 102, col);

  // Floor plinth.
  plate(g, 4, 172, 184, 18, COLORS.steel1, 3, 2);
  hazardStripes(g, 14, 178, 164, 6, 0x8f7426, 0x121418, 10);
  softEllipse(g, 96, 170, 70, 6, col, 0.25);
  c.tex.refresh();
}

// ───────────────────────────── Repair bay ─────────────────────────────

function repairBay(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.repairBay, 160, 160);
  if (!c) return;
  const g = c.g;
  const heal = COLORS.heal;
  // Gantry pillars + top beam.
  for (const x of [8, 136]) plate(g, x, 26, 16, 118, COLORS.steel2, 3, 2);
  plate(g, 4, 14, 152, 18, COLORS.steel3, 3, 2);
  hazardStripes(g, 30, 19, 100, 6, 0x8f7426, 0x121418, 9);
  // Down-light emitter + beam.
  plate(g, 62, 30, 36, 12, COLORS.steel2, 2, 2);
  const beam = g.createLinearGradient(0, 42, 0, 140);
  beam.addColorStop(0, rgba(heal, 0.3));
  beam.addColorStop(1, rgba(heal, 0.02));
  g.fillStyle = beam;
  g.beginPath();
  g.moveTo(66, 42);
  g.lineTo(94, 42);
  g.lineTo(118, 140);
  g.lineTo(42, 140);
  g.closePath();
  g.fill();
  px(g, 66, 41, 28, 2, heal, 0.95);
  // Hanging service arms.
  for (const [x0, x1, flip] of [
    [24, 44, 1],
    [136, 116, -1],
  ] as const) {
    g.lineCap = 'round';
    g.strokeStyle = rgba(INK);
    g.lineWidth = 7;
    g.beginPath();
    g.moveTo(x0, 40);
    g.lineTo(x1, 70);
    g.lineTo(x1 + flip * 6, 92);
    g.stroke();
    g.strokeStyle = rgba(COLORS.steel3);
    g.lineWidth = 4;
    g.stroke();
    circlePath(g, x1, 70, 4);
    g.fillStyle = rgba(COLORS.steel2);
    g.fill();
    g.strokeStyle = rgba(INK);
    g.lineWidth = 1.5;
    g.stroke();
    lamp(g, x1 + flip * 6, 94, heal, 1.6, 6);
  }
  // Holographic repair cross in the beam.
  g.save();
  g.translate(80, 84);
  const cross = () => {
    g.beginPath();
    g.rect(-6, -17, 12, 34);
    g.rect(-17, -6, 34, 12);
  };
  cross();
  g.fillStyle = rgba(heal, 0.25);
  g.fill();
  g.strokeStyle = rgba(heal, 0.95);
  g.lineWidth = 2;
  g.beginPath();
  g.moveTo(-6, -17);
  g.lineTo(6, -17);
  g.lineTo(6, -6);
  g.lineTo(17, -6);
  g.lineTo(17, 6);
  g.lineTo(6, 6);
  g.lineTo(6, 17);
  g.lineTo(-6, 17);
  g.lineTo(-6, 6);
  g.lineTo(-17, 6);
  g.lineTo(-17, -6);
  g.lineTo(-6, -6);
  g.closePath();
  g.stroke();
  g.restore();
  softDot(g, 80, 84, 30, heal, 0.25);
  // Floor pad.
  plate(g, 0, 140, 160, 18, COLORS.steel1, 3, 2);
  roundRectPath(g, 34, 136, 92, 8, 3);
  g.fillStyle = rgba(INK);
  g.fill();
  px(g, 38, 138, 84, 3, heal, 0.85);
  softEllipse(g, 80, 139, 56, 7, heal, 0.4);
  for (const x of [20, 140]) lamp(g, x, 149, heal, 1.5, 5);
  c.tex.refresh();
}

// ───────────────────────────── Hologram pedestal ─────────────────────────────

function holoBase(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.holoBase, 96, 40);
  if (!c) return;
  const g = c.g;
  const col = mix(COLORS.volt, 0xffffff, 0.2);
  // Upward emitter cone (faint; the item hologram sits above).
  const cone = g.createLinearGradient(0, 2, 0, 20);
  cone.addColorStop(0, rgba(col, 0));
  cone.addColorStop(1, rgba(col, 0.28));
  g.fillStyle = cone;
  g.beginPath();
  g.moveTo(30, 0);
  g.lineTo(66, 0);
  g.lineTo(58, 20);
  g.lineTo(38, 20);
  g.closePath();
  g.fill();
  // Pedestal body (trapezoid).
  polyPath(g, [14, 22, 82, 22, 92, 37, 4, 37]);
  const bg = g.createLinearGradient(0, 22, 0, 37);
  bg.addColorStop(0, rgba(COLORS.steel4));
  bg.addColorStop(0.2, rgba(COLORS.steel3));
  bg.addColorStop(1, rgba(COLORS.steel1));
  g.fillStyle = bg;
  g.fill();
  g.strokeStyle = rgba(INK);
  g.lineWidth = 2;
  g.stroke();
  // Emitter lens.
  roundRectPath(g, 34, 17, 28, 7, 3);
  g.fillStyle = rgba(INK);
  g.fill();
  px(g, 37, 19, 22, 3, col, 0.95);
  softEllipse(g, 48, 20, 22, 5, col, 0.5);
  // Side lights.
  for (const x of [18, 78]) px(g, x - 3, 29, 6, 2, col, 0.8);
  px(g, 10, 34, 76, 1, INK, 0.5);
  c.tex.refresh();
}

// ───────────────────────────── Scrap / health ─────────────────────────────

function scrap(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.scrap, 24, 24);
  if (!c) return;
  const g = c.g;
  const metal = COLORS.scrap;
  // Gear fragment (partial cog) + bent plate, ink outline.
  g.save();
  g.translate(10, 13);
  g.beginPath();
  const teeth = 8;
  for (let i = 0; i <= teeth * 2; i++) {
    const a = -0.4 + (i / (teeth * 2)) * TAU * 0.78;
    const r = i % 2 === 0 ? 7.5 : 5.8;
    const x = Math.cos(a) * r;
    const y = Math.sin(a) * r;
    if (i === 0) g.moveTo(0, 0);
    g.lineTo(x, y);
  }
  g.closePath();
  const gg = g.createLinearGradient(-7, -7, 7, 7);
  gg.addColorStop(0, rgba(tone(metal, 0.45)));
  gg.addColorStop(0.5, rgba(metal));
  gg.addColorStop(1, rgba(tone(metal, -0.45)));
  g.fillStyle = gg;
  g.fill();
  g.strokeStyle = rgba(INK);
  g.lineWidth = 1.5;
  g.stroke();
  circlePath(g, 0, 0, 2);
  g.fillStyle = rgba(INK);
  g.fill();
  g.restore();
  // Bent plate shard.
  polyPath(g, [13, 15, 21, 11, 23, 18, 15, 21]);
  g.fillStyle = rgba(mix(COLORS.steel4, metal, 0.3));
  g.fill();
  g.strokeStyle = rgba(INK);
  g.lineWidth = 1.5;
  g.stroke();
  // Glint.
  softDot(g, 7, 8, 5, 0xffffff, 0.8, 0.15);
  px(g, 6, 7, 2, 2, 0xffffff);
  px(g, 3, 7.5, 8, 1, 0xffffff, 0.6);
  px(g, 6.5, 4, 1, 8, 0xffffff, 0.6);
  c.tex.refresh();
}

function health(scene: Phaser.Scene): void {
  const c = newCanvas(scene, TEX.health, 28, 28);
  if (!c) return;
  const g = c.g;
  const heal = COLORS.heal;
  softDot(g, 14, 14, 14, heal, 0.35);
  // Capsule body (horizontal pill), glass with green fluid.
  roundRectPath(g, 3, 8, 22, 12, 6);
  g.fillStyle = rgba(INK);
  g.fill();
  roundRectPath(g, 4.5, 9.5, 19, 9, 4.5);
  const fg = g.createLinearGradient(0, 9.5, 0, 18.5);
  fg.addColorStop(0, rgba(tone(heal, 0.55)));
  fg.addColorStop(0.5, rgba(heal));
  fg.addColorStop(1, rgba(tone(heal, -0.45)));
  g.fillStyle = fg;
  g.fill();
  // Metal end caps.
  for (const x of [4.5, 19.5]) {
    roundRectPath(g, x, 9.5, 4, 9, 1.5);
    g.fillStyle = rgba(COLORS.steel4);
    g.fill();
  }
  // White cross.
  px(g, 12.5, 10.5, 3, 7, 0xffffff);
  px(g, 10.5, 12.5, 7, 3, 0xffffff);
  // Glass highlight.
  px(g, 8, 10.5, 12, 1, 0xffffff, 0.55);
  c.tex.refresh();
}

// ───────────────────────────── Orbs ─────────────────────────────

/**
 * Mystery orb shells (96×96): glassy sphere with inner glow and a type-specific rim.
 * The "?" is rendered in-game by loot, so the centre is left clean.
 */
export function buildOrbs(scene: Phaser.Scene): void {
  orb(scene, TEX.orbShell.arsenal, COLORS.orbArsenal, 'arsenal');
  orb(scene, TEX.orbShell.armor, COLORS.orbArmor, 'armor');
  orb(scene, TEX.orbShell.unstable, COLORS.orbUnstable, 'unstable');
}

function orb(scene: Phaser.Scene, key: string, col: number, kind: 'arsenal' | 'armor' | 'unstable'): void {
  const c = newCanvas(scene, key, 96, 96);
  if (!c) return;
  const g = c.g;
  const cx = 48;
  const cy = 48;
  const R = 29;
  // Outer aura.
  softDot(g, cx, cy, 48, col, 0.3);

  // Rim shapes (behind + around the sphere).
  if (kind === 'arsenal') {
    // Gold crosshair ring: ring with four bracketed ticks.
    g.beginPath();
    g.arc(cx, cy, 38, 0, TAU);
    g.strokeStyle = rgba(INK, 0.9);
    g.lineWidth = 6;
    g.stroke();
    for (let i = 0; i < 4; i++) {
      const a0 = (i / 4) * TAU + 0.28;
      g.beginPath();
      g.arc(cx, cy, 38, a0, a0 + TAU / 4 - 0.56);
      neonStroke(g, col, 2.4, 3, 1, 0xffffff, 0.45);
    }
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * TAU;
      const ca = Math.cos(a);
      const sa = Math.sin(a);
      g.beginPath();
      g.moveTo(cx + ca * 33, cy + sa * 33);
      g.lineTo(cx + ca * 45, cy + sa * 45);
      g.strokeStyle = rgba(INK);
      g.lineWidth = 6;
      g.lineCap = 'round';
      g.stroke();
      neonStroke(g, col, 3, 3, 1, 0xffffff, 0.6);
    }
  } else if (kind === 'armor') {
    // Green hex plating ring: six armour plates around the sphere.
    hexPath(g, cx, cy, 42, Math.PI / 6);
    g.strokeStyle = rgba(INK, 0.9);
    g.lineWidth = 7;
    g.stroke();
    for (let i = 0; i < 6; i++) {
      const a0 = -Math.PI / 2 + (i / 6) * TAU + Math.PI / 6;
      const a1 = a0 + TAU / 6;
      const p = (a: number, r: number) => [cx + Math.cos(a) * r, cy + Math.sin(a) * r];
      const [x0, y0] = p(a0 + 0.12, 43);
      const [x1, y1] = p(a1 - 0.12, 43);
      const [x2, y2] = p(a1 - 0.14, 35);
      const [x3, y3] = p(a0 + 0.14, 35);
      polyPath(g, [x0, y0, x1, y1, x2, y2, x3, y3]);
      g.fillStyle = rgba(mix(col, INK, 0.62));
      g.fill();
      g.strokeStyle = rgba(INK);
      g.lineWidth = 1.5;
      g.stroke();
      g.beginPath();
      g.moveTo(x0, y0);
      g.lineTo(x1, y1);
      neonStroke(g, col, 1.6, 2.5, 1, 0xffffff, 0.4);
    }
  } else {
    // Magenta jagged glitch ring: broken, offset arc fragments with spikes.
    const frags = 9;
    for (let i = 0; i < frags; i++) {
      const a0 = (i / frags) * TAU + (i % 2 ? 0.05 : -0.04);
      const a1 = a0 + TAU / frags - 0.22;
      const r = 37 + (i % 3) * 3 - 2;
      g.beginPath();
      g.arc(cx + (i % 2 ? 1.5 : -1), cy, r, a0, a1);
      g.strokeStyle = rgba(INK, 0.85);
      g.lineWidth = 5;
      g.stroke();
      neonStroke(g, col, 2, 3, 1, 0xffffff, 0.45);
      // Spike at the end of every other fragment.
      if (i % 2 === 0) {
        const a = a1;
        g.beginPath();
        g.moveTo(cx + Math.cos(a) * (r - 4), cy + Math.sin(a) * (r - 4));
        g.lineTo(cx + Math.cos(a + 0.06) * (r + 9), cy + Math.sin(a + 0.06) * (r + 9));
        neonStroke(g, col, 1.6, 2, 1, 0xffffff, 0.5);
      }
    }
    // Glitch slices: horizontal offset bars.
    for (const [y, x, w] of [
      [20, 60, 18],
      [70, 14, 14],
      [77, 58, 10],
    ]) {
      px(g, x, y, w, 2, col, 0.85);
      px(g, x + 3, y + 2, w - 4, 1, 0x35e8ff, 0.5);
    }
  }

  // Sphere: dark tinted glass with a strong fresnel rim.
  circlePath(g, cx, cy, R + 1.5);
  g.fillStyle = rgba(INK, 0.9);
  g.fill();
  circlePath(g, cx, cy, R);
  const body = g.createRadialGradient(cx - 6, cy - 8, 2, cx, cy, R);
  body.addColorStop(0, rgba(mix(col, 0xffffff, 0.35), 0.95));
  body.addColorStop(0.35, rgba(mix(col, INK, 0.25), 0.9));
  body.addColorStop(0.8, rgba(mix(col, INK, 0.7), 0.95));
  body.addColorStop(1, rgba(mix(col, 0xffffff, 0.2), 1));
  g.fillStyle = body;
  g.fill();
  // Inner glow core (where the "?" sits).
  softDot(g, cx, cy + 2, 20, mix(col, 0xffffff, 0.5), 0.55);
  // Inner swirl wisps.
  g.save();
  circlePath(g, cx, cy, R - 1);
  g.clip();
  for (let k = 0; k < 3; k++) {
    g.beginPath();
    g.ellipse(cx, cy, R * (0.9 - k * 0.2), R * (0.4 - k * 0.08), -0.5 + k * 0.9, 0, Math.PI * 1.3);
    g.strokeStyle = rgba(0xffffff, 0.13);
    g.lineWidth = 2;
    g.stroke();
  }
  // Bottom bounce light.
  softEllipse(g, cx, cy + R * 0.75, R * 0.7, R * 0.3, col, 0.5);
  g.restore();
  // Rim light.
  circlePath(g, cx, cy, R - 0.5);
  g.strokeStyle = rgba(mix(col, 0xffffff, 0.45), 0.9);
  g.lineWidth = 1.6;
  g.stroke();
  // Specular highlights (key light upper left).
  g.save();
  g.beginPath();
  g.ellipse(cx - 11, cy - 14, 10, 5.5, -0.6, 0, TAU);
  const sg = g.createRadialGradient(cx - 12, cy - 15, 0, cx - 11, cy - 14, 10);
  sg.addColorStop(0, 'rgba(255,255,255,0.95)');
  sg.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = sg;
  g.fill();
  g.restore();
  circlePath(g, cx + 15, cy + 13, 2);
  g.fillStyle = 'rgba(255,255,255,0.5)';
  g.fill();
  c.tex.refresh();
}
